import { ExternalLink, X } from "lucide-react";

/**
 * Bulletproof Stripe redirect fallback for mobile preview iframes where
 * popups and top-level navigation are blocked.
 *
 * Renders a centered overlay with a giant emerald-green button containing
 * a real <a href={url} target="_blank">. Tapping the button uses a fresh
 * user-gesture so the browser opens a new tab even if window.open is
 * being silently swallowed.
 */
export default function StripeOpenModal({ url, amountLabel, onClose }) {
  if (!url) return null;
  return (
    <div
      data-testid="stripe-open-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm px-5"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="stripe-open-modal-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center justify-center"
        >
          <X className="w-4 h-4"/>
        </button>

        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Secure checkout</div>
        <h3 className="font-display mt-2 text-2xl font-extrabold text-slate-950">Open Stripe to complete payment</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {amountLabel ? <>You'll be redirected to Stripe to pay <span className="font-semibold text-slate-900">{amountLabel}</span>. </> : "You'll be redirected to Stripe to finish checkout. "}
          We never see or store your card. Cancel anytime.
        </p>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="stripe-open-modal-cta"
          onClick={() => { setTimeout(() => onClose?.(), 600); }}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-lg shadow-emerald-900/30"
        >
          Open Stripe Checkout <ExternalLink className="w-4 h-4"/>
        </a>

        <div className="mt-4 text-[11px] text-slate-500 text-center">
          Powered by Stripe · PCI-DSS · TLS 1.3
        </div>
      </div>
    </div>
  );
}
